import * as React from "react";
import {
  List,
  DataTable,
  TextField,
  ReferenceField,
  ReferenceInput,
  NumberField,
  SelectArrayInput,
  DateField,
  useRecordContext,
  downloadCSV,
  DateTimeInput,
  AutocompleteInput,
  usePermissions,
  FunctionField,
} from "react-admin";
import { ROLE_TYPES } from "../enums/Roles";
import { COUPON_STATUS } from "../enums/CouponStatus";
import jsonExport from "jsonexport/dist";
import {
  getDateFieldByStatus,
  getRestaurantNameFieldByStatus,
  getRestaurantAddressFieldByStatus,
  getProductNameFieldByStatus,
  getProductPriceFieldByStatus,
  getProductDiscountFieldByStatus,
} from "../utils/CouponUtils";
import { formatDateFromISODate } from "../utils/DateTimeUtils";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { endOfDay, startOfDay, subMonths } from "date-fns";

const CouponFilter = () => {
  const { permissions } = usePermissions();
  return [
    <SelectArrayInput
      optionText="name"
      choices={[
        { id: "AVAILABLE", name: "Guardado" },
        { id: "ACTIVATED", name: "Activado" },
        { id: "REDEEMED", name: "Canjeado" },
        { id: "INACTIVE", name: "Inactivo" },
      ]}
      label="Estado"
      source="status"
    />,
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <ReferenceInput
            source="restaurant"
            reference="restaurants"
            perPage={100}
            filter={{
              type: RESTAURANT_TYPES.REGULAR,
            }}
            label="Restaurante"
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ name: searchText })}
              label="Restaurante"
              optionText="name"
            />
          </ReferenceInput>,
        ]
      : []),
    <DateTimeInput
      source="redeemedDateFrom"
      label="Fecha Desde"
      alwaysOn={permissions === ROLE_TYPES.RESTAURANT}
    />,
    <DateTimeInput
      source="redeemedDateTo"
      label="Fecha Hasta"
      alwaysOn={permissions === ROLE_TYPES.RESTAURANT}
    />,
  ];
};

const CustomDateField = () => {
  const record = useRecordContext();
  let source = "createdAt";
  if (record.status === COUPON_STATUS.REDEEMED) {
    source = "redeemedCoupon.redeemedDate";
  } else if (record.status === COUPON_STATUS.ACTIVATED) {
    source = "activationDate";
  } else if (record.status === COUPON_STATUS.INACTIVE) {
    source = "deactivationDate";
  }
  return (
    <DateField
      source={source}
      label="Fecha"
      showTime={true}
      sortBy={source}
      sortByOrder="DESC"
    />
  );
};

const CustomPriceField = () => {
  const record = useRecordContext();
  if (record.status === COUPON_STATUS.REDEEMED) {
    return (
      <NumberField
        source="redeemedCoupon.product.price"
        label="Precio Original"
        options={{
          style: "currency",
          currency: "ARS",
          currencyDisplay: "narrowSymbol",
        }}
        sortBy={record.redeemedCoupon.product.price}
      />
    );
  }
  return (
    <ReferenceField
      label="Precio Original"
      reference="products"
      source="product._id"
      link={false}
    >
      <NumberField
        source="price"
        label="Precio Original"
        options={{
          style: "currency",
          currency: "ARS",
          currencyDisplay: "narrowSymbol",
        }}
      />
    </ReferenceField>
  );
};

const CustomDiscountField = () => {
  const record = useRecordContext();
  if (record.status === COUPON_STATUS.REDEEMED) {
    return (
      <NumberField
        source="redeemedCoupon.product.discount"
        label="Descuento"
        options={{ style: "percent" }}
        sortBy={record.redeemedCoupon.product.discount}
      />
    );
  }
  return <span>-</span>;
};

const adminExporter = (coupons) => {
  const couponsForExport = coupons.map((coupon) => {
    const couponForExport = {
      Fecha: formatDateFromISODate(getDateFieldByStatus(coupon)),
      Estado: coupon.status,
      Restaurante: getRestaurantNameFieldByStatus(coupon),
      Direccion: getRestaurantAddressFieldByStatus(coupon),
      Producto: getProductNameFieldByStatus(coupon),
      "Precio Original": getProductPriceFieldByStatus(coupon),
      Descuento: getProductDiscountFieldByStatus(coupon),
      Usuario: coupon.customer,
    };
    return couponForExport;
  });
  jsonExport(
    couponsForExport,
    {
      headers: [
        "Fecha",
        "Estado",
        "Restaurante",
        "Direccion",
        "Producto",
        "Precio Original",
        "Descuento",
        "Usuario",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, "coupons");
    }
  );
};

const restaurantExporter = (coupons) => {
  const couponsForExport = coupons
    .filter((c) => c.status === COUPON_STATUS.REDEEMED)
    .map((coupon) => {
      const couponForExport = {
        Fecha: formatDateFromISODate(coupon?.redeemedCoupon?.redeemedDate),
        Producto: coupon?.redeemedCoupon?.product?.name,
        "Precio Original": coupon?.redeemedCoupon?.product?.price,
        Descuento: coupon?.redeemedCoupon?.product?.discount,
        "Precio Final": (
          coupon?.redeemedCoupon?.product?.price -
          coupon?.redeemedCoupon?.product?.price *
            coupon?.redeemedCoupon?.product?.discount
        ).toLocaleString("en-US", {
          style: "currency",
          currency: "ARS",
          currencyDisplay: "narrowSymbol",
        }),
      };
      return couponForExport;
    });
  jsonExport(
    couponsForExport,
    {
      headers: [
        "Fecha",
        "Producto",
        "Precio Original",
        "Descuento",
        "Precio Final",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, "coupons");
    }
  );
};

const CouponList = () => {
  const { permissions } = usePermissions();
  return (
    <List
      title="Canjes"
      exporter={
        permissions === ROLE_TYPES.ADMIN ? adminExporter : restaurantExporter
      }
      filters={CouponFilter()}
      {...(permissions === ROLE_TYPES.RESTAURANT && {
        filterDefaultValues: {
          redeemedDateFrom: subMonths(startOfDay(new Date()), 1),
          redeemedDateTo: endOfDay(new Date()),
        },
      })}
      {...(permissions === ROLE_TYPES.RESTAURANT && {
        filter: {
          status: [COUPON_STATUS.REDEEMED],
        },
      })}
    >
      <DataTable bulkActionButtons={false}>
        <DataTable.Col label="Fecha">
          <CustomDateField label="Fecha" />
        </DataTable.Col>
        {permissions === ROLE_TYPES.ADMIN && (
          <DataTable.Col source="status" label="Estado">
            <TextField source="status" label="Estado" />
          </DataTable.Col>
        )}
        {permissions === ROLE_TYPES.ADMIN && (
          <DataTable.Col source="restaurant._id" label="Restaurante">
            <ReferenceField
              label="Restaurante"
              reference="restaurants"
              source="restaurant._id"
              link="show"
            >
              <TextField source="name" label="Nombre" />
            </ReferenceField>
          </DataTable.Col>
        )}
        <DataTable.Col source="product._id" label="Producto">
          <ReferenceField
            label="Producto"
            reference="products"
            source="product._id"
            link="show"
          >
            <TextField source="name" label="Precio" />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col label="Precio Original">
          <CustomPriceField label="Precio Original" />
        </DataTable.Col>
        <DataTable.Col label="Descuento">
          <CustomDiscountField label="Descuento" />
        </DataTable.Col>
        {permissions === ROLE_TYPES.RESTAURANT && (
          <DataTable.Col label={"Precio Final"}>
            <FunctionField
              render={(record) =>
                (
                  record?.redeemedCoupon?.product?.price -
                  record?.redeemedCoupon?.product?.price *
                    record?.redeemedCoupon?.product?.discount
                ).toLocaleString("en-US", {
                  style: "currency",
                  currency: "ARS",
                  currencyDisplay: "narrowSymbol",
                })
              }
              label={"Precio Final"}
            />
          </DataTable.Col>
        )}
        {permissions === ROLE_TYPES.ADMIN && (
          <DataTable.Col source="customer" label="Usuario">
            <ReferenceField
              label="Usuario"
              source="customer"
              reference="users"
              link="show"
            >
              <TextField source="email" label="Usuario" />
            </ReferenceField>
          </DataTable.Col>
        )}
      </DataTable>
    </List>
  );
};

export default CouponList;
